/**
 * Timezone Utility Functions
 * Handles timezone conversion and formatting for Muscat, Oman (UTC+4)
 */

// Muscat timezone (UTC+4)
export const MUSCAT_TIMEZONE = 'Asia/Muscat';

/**
 * Format a date/time string to Muscat timezone
 * @param dateString - ISO date string or timestamp
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string in Muscat timezone
 */
export const formatToMuscatTime = (
  dateString: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }
): string => {
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return new Intl.DateTimeFormat('en-GB', {
      ...options,
      timeZone: MUSCAT_TIMEZONE
    }).format(date);
  } catch (error) {
    console.error('Error formatting date to Muscat time:', error);
    return 'Invalid Date';
  }
};

/**
 * Get current time in Muscat timezone
 * @returns Current date/time in Muscat timezone
 */
export const getCurrentMuscatTime = (): Date => {
  const now = new Date();
  const muscatTime = new Date(now.toLocaleString('en-US', { timeZone: MUSCAT_TIMEZONE }));
  return muscatTime;
};

/**
 * Format date for display in the application
 * @param dateString - ISO date string or timestamp
 * @returns Formatted date string (DD/MM/YYYY HH:MM:SS)
 */
export const formatDisplayDate = (dateString: string | Date): string => {
  return formatToMuscatTime(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Format date for display (date only)
 * @param dateString - ISO date string or timestamp
 * @returns Formatted date string (DD/MM/YYYY)
 */
export const formatDisplayDateOnly = (dateString: string | Date): string => {
  return formatToMuscatTime(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format time for display (time only)
 * @param dateString - ISO date string or timestamp
 * @returns Formatted time string (HH:MM:SS)
 */
export const formatDisplayTime = (dateString: string | Date): string => {
  return formatToMuscatTime(dateString, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 * @param dateString - ISO date string or timestamp
 * @returns Relative time string
 */
export const getRelativeTime = (dateString: string | Date): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDisplayDate(date);
    }
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return 'Invalid Date';
  }
};

/**
 * Check if a date is today in Muscat timezone
 * @param dateString - ISO date string or timestamp
 * @returns True if the date is today
 */
export const isToday = (dateString: string | Date): boolean => {
  try {
    const date = new Date(dateString);
    const today = new Date();
    
    const dateInMuscat = new Date(date.toLocaleString('en-US', { timeZone: MUSCAT_TIMEZONE }));
    const todayInMuscat = new Date(today.toLocaleString('en-US', { timeZone: MUSCAT_TIMEZONE }));
    
    return dateInMuscat.toDateString() === todayInMuscat.toDateString();
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};

/**
 * Get timezone offset information
 * @returns Object with timezone information
 */
export const getTimezoneInfo = () => {
  const now = new Date();
  const muscatTime = new Date(now.toLocaleString('en-US', { timeZone: MUSCAT_TIMEZONE }));
  const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
  
  return {
    timezone: MUSCAT_TIMEZONE,
    offset: '+04:00',
    currentMuscatTime: formatDisplayDate(muscatTime),
    currentUTCTime: formatDisplayDate(utcTime),
    offsetHours: 4
  };
};
